import React from 'react';
import {
    Form,
    Input,
    DatePicker,
    Select,
    InputNumber,
    Typography
} from 'antd';
import { Rule } from 'antd/es/form';
import DynamicSelect from './DynamicSelect';
import dayjs from 'dayjs';
import {FieldMetadata} from "@/modules/customer-category/types";
import {CustomerCategoryDTO, CustomerCategoryFieldDTO} from "@/shared/api/model";

const { Option } = Select;

export interface DynamicFormFieldProps {
    fieldName: string;
    metadata: FieldMetadata & { selectProvider?: string };
    fieldConfig?: CustomerCategoryFieldDTO;
    category?: CustomerCategoryDTO | null;
    entityType?: string;
}

const DynamicFormField: React.FC<DynamicFormFieldProps> = ({
    fieldName,
    metadata,
    fieldConfig,
    category,
    entityType = 'Customer'
}) => {
    // Determine field visibility and requirements
    const isVisible = fieldConfig ? fieldConfig.isVisible : true;
    const isMandatory = fieldConfig ? fieldConfig.isMandatory : metadata.defaultRequired;
    const label = fieldConfig ? fieldConfig.customLabel : metadata.label;

    if (!isVisible) return null;

    // Prepare validation rules
    const rules: Rule[] = isMandatory ? [{ required: true, message: `Please enter ${label}` }] : [];

    // Render field based on type
    const renderFieldInput = () => {
        switch (metadata.type) {
            case 'text':
                return <Input placeholder={`Enter ${label}`} />;

            case 'date':
                return (
                    <DatePicker 
                        style={{ width: '100%' }} 
                        placeholder={`Select ${label}`}
                    />
                );

            case 'select':
                const selectProvider = metadata.selectProvider;
                
                if (selectProvider) {
                    return (
                        <DynamicSelect
                            providerName={selectProvider}
                            fieldName={fieldName}
                            entityType={entityType}
                            categoryId={category?.id}
                            placeholder={`Select ${label}`}
                            style={{ width: '100%' }}
                            showDescriptions={true}
                        />
                    );
                } else {
                    // Fallback to static select for fields without providers
                    return (
                        <Select placeholder={`Select ${label}`}>
                            <Option value="option1">Option 1</Option>
                            <Option value="option2">Option 2</Option>
                            <Option value="option3">Option 3</Option>
                        </Select>
                    );
                }

            case 'phone':
                return (
                    <Input 
                        placeholder={`Enter ${label}`}
                        addonBefore="+"
                    />
                );

            case 'number':
                return (
                    <InputNumber 
                        style={{ width: '100%' }} 
                        placeholder={`Enter ${label}`}
                    />
                );

            case 'email':
                return (
                    <Input 
                        type="email"
                        placeholder={`Enter ${label}`}
                    />
                );

            case 'textarea':
                return (
                    <Input.TextArea 
                        rows={3}
                        placeholder={`Enter ${label}`}
                    />
                );

            default:
                return <Input placeholder={`Enter ${label}`} />;
        }
    };

    // Handle special validation rules
    const getValidationRules = (): Rule[] => {
        const baseRules = [...rules];

        switch (metadata.type) {
            case 'phone':
                baseRules.push({
                    pattern: /^\+?[0-9]{10,15}$/,
                    message: 'Please enter a valid phone number'
                });
                break;

            case 'email':
                baseRules.push({
                    type: 'email',
                    message: 'Please enter a valid email address'
                });
                break;

            case 'number':
                baseRules.push({
                    type: 'number',
                    message: 'Please enter a valid number'
                });
                break;
        }

        return baseRules;
    };

    // Handle special form item props
    const getFormItemProps = () => {
        const baseProps = {
            name: fieldName,
            label,
            rules: getValidationRules()
        };

        // Special handling for date fields
        if (metadata.type === 'date') {
            return {
                ...baseProps,
                getValueProps: (value: any) => ({ value: value ? dayjs(value) : undefined }),
                normalize: (value: any) => value ? value.format('YYYY-MM-DD') : undefined
            };
        }

        // Special handling for number fields
        if (metadata.type === 'number') {
            return {
                ...baseProps,
                normalize: (value: any) => value ? Number(value) : undefined
            };
        }

        return baseProps;
    };

    return (
        <Form.Item {...getFormItemProps()}>
            {renderFieldInput()}
        </Form.Item>
    );
};

export default DynamicFormField;
